import { useCallback, useEffect, useState } from 'react'
import type { CreateOrganizationDTO, UpdateOrganizationDTO } from 'types/organization'
import type { CreateUserDTO, UpdateUserDTO, UserData } from 'types/user'
import type { OrganizationRolePair } from 'types/role'

interface UseOrganizationActionsProps {
  createOrganization: (dto: CreateOrganizationDTO) => void | Promise<void>
  deleteOrganization: (id: string) => Promise<void>
  updateOrganization: (id: string, dto: UpdateOrganizationDTO) => Promise<void>
  createUser: (dto: CreateUserDTO) => Promise<any>
  updateUser: (id: string, dto: UpdateUserDTO) => Promise<any>
  isCreatingOrg: boolean
  isDeletingOrg: boolean
  isUpdatingOrg: boolean
  isCreatingUser: boolean
  isUpdatingUser: boolean
  createdOrganization: any
  onRefreshNeeded: () => void
}

export const useOrganizationActions = ({
  createOrganization,
  deleteOrganization,
  updateOrganization,
  createUser,
  updateUser,
  isCreatingOrg,
  isDeletingOrg,
  isUpdatingOrg,
  isCreatingUser,
  isUpdatingUser,
  createdOrganization,
  onRefreshNeeded,
}: UseOrganizationActionsProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (createdOrganization) {
      onRefreshNeeded()
    }
  }, [createdOrganization, onRefreshNeeded])

  const handleOrganizationCreated = useCallback(
    (organizationDTO: CreateOrganizationDTO): Promise<void> => {
      return new Promise<void>((resolve) => {
        if (isSubmitting || isCreatingOrg) {
          resolve()
          return
        }

        setIsSubmitting(true)
        createOrganization(organizationDTO)
        resolve()
      })
    },
    [createOrganization, isCreatingOrg, isSubmitting]
  )

  const handleOrganizationDeleted = useCallback(
    (organizationId: string): Promise<void> => {
      return new Promise<void>((resolve) => {
        if (isSubmitting || isDeletingOrg) {
          resolve()
          return
        }

        setIsSubmitting(true)
        deleteOrganization(organizationId)
          .then(() => {
            onRefreshNeeded()
          })
          .finally(() => {
            setIsSubmitting(false)
            resolve()
          })
      })
    },
    [deleteOrganization, isDeletingOrg, isSubmitting, onRefreshNeeded]
  )

  const handleOrganizationUpdated = useCallback(
    (organizationId: string, updateDTO: UpdateOrganizationDTO): Promise<void> => {
      return new Promise<void>((resolve) => {
        if (isSubmitting || isUpdatingOrg) {
          resolve()
          return
        }

        setIsSubmitting(true)
        updateOrganization(organizationId, updateDTO)
          .then(() => {
            onRefreshNeeded()
          })
          .catch((error) => {
            console.error('Error updating organization:', error)
          })
          .finally(() => {
            setIsSubmitting(false)
            resolve()
          })
      })
    },
    [updateOrganization, isSubmitting, isUpdatingOrg, onRefreshNeeded]
  )

  const handleUserAction = useCallback(
    (userData: UserData): Promise<void> => {
      return new Promise<void>((resolve) => {
        if (isSubmitting || isCreatingUser || isUpdatingUser) {
          console.log('User action already in progress')
          resolve()
          return
        }

        setIsSubmitting(true)

        // If only ID is provided, it's a deletion confirmation
        if (userData.id && Object.keys(userData).length === 1) {
          onRefreshNeeded()
          setIsSubmitting(false)
          resolve()
          return
        }

        const roles: Array<OrganizationRolePair> = userData.roles

        if (userData.id) {
          // This is an update operation
          const updateUserDTO: UpdateUserDTO = {
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            position: userData.position,
            roles,
            ledgerAccountId: userData.ledgerAccountId,
          }

          updateUser(userData.id, updateUserDTO)
            .then(() => {
              onRefreshNeeded()
            })
            .catch((error) => {
              console.error('Error updating user:', error)
            })
            .finally(() => {
              setIsSubmitting(false)
              resolve()
            })
        } else {
          // This is a create operation
          const createUserDTO: CreateUserDTO = {
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            position: userData.position,
            startDate: userData.startDate,
            roles,
            ledgerAccountId: userData.ledgerAccountId,
          }

          createUser(createUserDTO)
            .then(() => {
              onRefreshNeeded()
            })
            .catch((error) => {
              console.error('Error creating user:', error)
            })
            .finally(() => {
              setIsSubmitting(false)
              resolve()
            })
        }
      })
    },
    [createUser, updateUser, isSubmitting, isCreatingUser, isUpdatingUser, onRefreshNeeded]
  )

  return {
    isSubmitting,
    setIsSubmitting,
    handleOrganizationCreated,
    handleOrganizationDeleted,
    handleOrganizationUpdated,
    handleUserAction,
  }
}
